import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { api, authRequest } from './api';
import type { Locale } from './domain';

export interface AuthUser {
	id: string; email: string; name: string; kind: 'client' | 'staff'; locale: Locale; isSuperAdmin: boolean; twoFactorEnabled: boolean; passkeyEnabled: boolean;
}

interface AuthState {
	user: AuthUser | null;
	profile: { company?: string; phone?: string | null; messenger?: string | null } | null;
	permissions: { permission: string; mode: 'direct' | 'approval_required' | 'none' }[];
	loading: boolean;
	refresh: () => Promise<void>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<Omit<AuthState, 'refresh' | 'logout'>>({ user: null, profile: null, permissions: [], loading: true });
	const refresh = useCallback(async () => {
		try {
			const data = await api<{ user: AuthUser; profile: AuthState['profile']; permissions: AuthState['permissions'] }>('/api/v1/me');
			const pendingCompany = data.user.kind === 'client' ? localStorage.getItem('volt-pending-company') : null;
			if (pendingCompany) { await api('/api/v1/me/profile', { method: 'PATCH', body: JSON.stringify({ company: pendingCompany }) }); data.profile = { ...(data.profile ?? {}), company: pendingCompany }; localStorage.removeItem('volt-pending-company'); }
			setState({ ...data, loading: false });
		} catch { setState({ user: null, profile: null, permissions: [], loading: false }); }
	}, []);
	useEffect(() => { void refresh(); }, [refresh]);
	const logout = useCallback(async () => { await authRequest('/sign-out', {}); setState({ user: null, profile: null, permissions: [], loading: false }); }, []);
	const value = useMemo(() => ({ ...state, refresh, logout }), [state, refresh, logout]);
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const value = useContext(AuthContext);
	if (!value) throw new Error('useAuth must be used inside AuthProvider');
	return value;
}

export function ProtectedRoute({ children, staff = false, allowAny = false }: { children: ReactNode; staff?: boolean; allowAny?: boolean }) {
	const { user, loading } = useAuth();
	const location = useLocation();
	if (loading) return <div className="route-loading" role="status"><span /><p>Загружаем кабинет…</p></div>;
	if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
	if (user.kind === 'staff' && !user.twoFactorEnabled && !user.passkeyEnabled && location.pathname !== '/security/setup')
		return <Navigate to="/security/setup" replace />;
	if (!allowAny && staff && user.kind !== 'staff') return <Navigate to="/app" replace />;
	if (!allowAny && !staff && user.kind === 'staff') return <Navigate to="/admin" replace />;
	return children;
}
